import React, { useState } from 'react';
import {
  CogIcon,
  BuildingOfficeIcon,
  ClockIcon,
  BellIcon,
  UserIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { id: 'general', name: 'Général', icon: CogIcon },
    { id: 'studios', name: 'Studios', icon: BuildingOfficeIcon },
    { id: 'schedule', name: 'Horaires', icon: ClockIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'users', name: 'Utilisateurs', icon: UserIcon },
    { id: 'security', name: 'Sécurité', icon: ShieldCheckIcon },
    { id: 'billing', name: 'Facturation', icon: CurrencyDollarIcon }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Paramètres</h2>
        <p className="text-gray-600">Configurez votre studio et vos préférences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="card">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Paramètres généraux</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du studio
                    </label>
                    <input
                      type="text"
                      defaultValue="Nazaï Studio"
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email de contact
                    </label>
                    <input
                      type="email"
                      defaultValue="contact@nazaistudio.ma"
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      defaultValue="+212 5XX-XXX-XXX"
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fuseau horaire
                    </label>
                    <select className="input-field">
                      <option>Africa/Casablanca</option>
                      <option>Europe/Paris</option>
                      <option>UTC</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    defaultValue="Votre coach est à l'écoute de vos besoins, pour personnaliser le coaching. Boxe, Muay Thai, Kick Boxing - Cours privés professionnels."
                    className="input-field"
                  />
                </div>
              </div>
            )}

            {activeTab === 'studios' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">Studios</h3>
                  <button className="btn-primary">Ajouter un studio</button>
                </div>
                
                <div className="space-y-4">
                  {['Power Studio Plaza', 'Pulse Studio Plaza', 'Vitality Studio Plaza'].map((studio, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{studio}</h4>
                          <p className="text-sm text-gray-500">Plaza Shopping Center, Casablanca</p>
                          <p className="text-sm text-gray-500">Capacité: 1 personne par séance</p>
                        </div>
                        <div className="flex space-x-2">
                          <button className="text-primary-600 hover:text-primary-700 text-sm">
                            Modifier
                          </button>
                          <button className="text-error-600 hover:text-error-700 text-sm">
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'schedule' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Horaires d'ouverture</h3>
                
                <div className="space-y-4">
                  {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((day, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          defaultChecked={index < 6}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-3"
                        />
                        <span className="font-medium text-gray-900 w-20">{day}</span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          defaultValue="08:00"
                          className="input-field w-auto"
                          disabled={index === 6}
                        />
                        <span className="text-gray-500">à</span>
                        <input
                          type="time"
                          defaultValue="21:00"
                          className="input-field w-auto"
                          disabled={index === 6}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Nouvelles réservations</h4>
                      <p className="text-sm text-gray-500">Recevoir un email pour chaque nouvelle réservation</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Annulations</h4>
                      <p className="text-sm text-gray-500">Recevoir un email pour chaque annulation</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Rappels de séances</h4>
                      <p className="text-sm text-gray-500">Envoyer des rappels aux clients 24h avant</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Rapports hebdomadaires</h4>
                      <p className="text-sm text-gray-500">Recevoir un résumé des performances chaque semaine</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                  </div>
                </div>
              </div>
            )}

            {['users', 'security', 'billing'].includes(activeTab) && (
              <div className="text-center py-12">
                <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {tabs.find(t => t.id === activeTab)?.name}
                </h3>
                <p className="text-gray-500">Cette section sera bientôt disponible.</p>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button className="btn-primary">
                Enregistrer les modifications
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
